import React, { FC, useState } from 'react';
import { IKeywordSearchResult } from '../../shared/interfaces/IKeywordSearchResult';
import KeywordSearchResult from '../KeywordSearchResult/KeywordSearchResult';
import { Box, Button, Divider, Typography, useTheme } from '@mui/material';
import { IKeywordSearchResultResponse } from '../../shared/interfaces/IKeywordSearchResultResponse';
import { MoreVert, SkipNext, SkipPrevious } from '@mui/icons-material';
import Helper from '../../shared/helpers/Helper';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

interface Props {
  searchResults: IKeywordSearchResultResponse[];
  reloadData: () => void;
}

const KeywordDetail: FC<Props> = ({ searchResults, reloadData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [pageIndex, setPageIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);

  return (
    <>
      {searchResults.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ my: theme.spacing(4) }}>{t('no_records')}</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mx: theme.spacing(-2),
              px: theme.spacing(7),
              py: theme.spacing(2),
              backgroundColor:
                theme.palette.mode === 'light'
                  ? theme.palette.background.default
                  : theme.palette.background.paper,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Button
                disabled={pageIndex === searchResults.length - 1}
                variant="outlined"
                onClick={() => setPageIndex(pageIndex + 1)}
              >
                {pageIndex + 1 < searchResults.length &&
                  Helper.getFormattedDate(searchResults[pageIndex + 1].createdAt, true)}
                <SkipPrevious />
              </Button>
              <Typography variant="button">
                {Helper.getFormattedDate(searchResults[pageIndex].createdAt, true)}
              </Typography>
              <Button
                disabled={pageIndex === 0}
                variant="outlined"
                onClick={() => setPageIndex(pageIndex - 1)}
                sx={{ ml: theme.spacing(1) }}
              >
                {pageIndex - 1 >= 0 &&
                  Helper.getFormattedDate(searchResults[pageIndex - 1].createdAt, true)}
                <SkipNext />
              </Button>
            </Box>
          </Box>
          <Box sx={{ mt: theme.spacing(2) }}>
            {searchResults[pageIndex].results
              .slice(0, showAll ? searchResults[pageIndex].results.length : 10)
              .map((searchResult: IKeywordSearchResult) => {
                return (
                  <Box key={searchResult.id}>
                    <Divider />
                    <KeywordSearchResult
                      key={searchResult.id}
                      sentiment_score={searchResult.sentiment_score}
                      link={searchResult.link}
                      displayedLink={searchResult.displayed_link}
                      title={searchResult.title}
                      snippet={searchResult.snippet}
                      position_overall={searchResult.position_overall}
                      entry_id={searchResult.id}
                      id={searchResults[pageIndex].id}
                      sentiment_overridden={searchResult.sentiment_overridden}
                      original_sentiment_score={searchResult.original_sentiment_score}
                      entry={searchResult}
                      reloadData={reloadData}
                    />
                  </Box>
                );
              })}
            <Divider />
            <Box sx={{ pl: theme.spacing(13), mt: theme.spacing(4) }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<MoreVert />}
                onClick={() => setShowAll((prevState) => !prevState)}
              >
                {showAll ? `${t('show_less')}` : `${t('show_more')}`}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};
export default KeywordDetail;
